import { useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { getAmountsForExternalPayments, getFullUserInstallmentsAdvance, getUserInstallmentsApi } from '../utils/apisConsts'
import { request } from '../../../shared/src/apis';
import { Col, Descriptions, Row, Spin, message } from 'antd'
import helper from '../utils/helper'
import { errorMessage } from '../utils/commonStringsConsts'
import moment from 'moment'
import { paymentTypes } from '../utils/commonInterfaces'
import { DataTable } from '@operations/shared'

function UserSettlementDetails({ userInfo, paymentType, amount, isDiscountApplied, selectedPaymentCycle, fullEarlySettlement, setFullEarlySettlement }: { userInfo: { [key: string]: any }, paymentType: typeof paymentTypes[keyof typeof paymentTypes], amount: number, isDiscountApplied: boolean, selectedPaymentCycle: number, fullEarlySettlement: any, setFullEarlySettlement: (v: any) => void }) {
    const dispatch = useDispatch()
    const [installments, setInstallments] = useState<any[]>([])
    const [loadingTableData, setLoadingTableData] = useState(false)
    const [columns, setColumns] = useState<any[]>([])
    const [viewAmounts, setViewAmounts] = useState(false);
    const [dueAmount, setDueAmount] = useState({ afterDiscount: 0, beforeDiscount: 0 });
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 5,
        total: 0,
    })
    const [amounts, setAmounts] = useState({
        beforeAmount: 0,
        afterAmount: 0,
    })

    const canShowSettlementInfo = paymentType != null && !!selectedPaymentCycle;

    useEffect(() => {
        if (!canShowSettlementInfo) {
            setInstallments([])
            setColumns([])
            setViewAmounts(false)
            setPagination({ current: 1, pageSize: 5, total: 0 })
            return
        }
        if (paymentType === paymentTypes.fullSettlement) {
            setLoadingTableData(true)
            getFullInstallmentsAdvances(selectedPaymentCycle)
        } else if (paymentType === paymentTypes.partialSettlement) {
            setLoadingTableData(true)
            getInstallments(selectedPaymentCycle)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paymentType, selectedPaymentCycle])


    useEffect(() => {
        if (isDiscountApplied) {
                    setAmounts({
                        beforeAmount: dueAmount?.afterDiscount,
                        afterAmount: dueAmount?.afterDiscount
                    });
                    setFullEarlySettlement({
                        ...fullEarlySettlement,
                        dueAmount: dueAmount?.afterDiscount
                    })
                } else {
                    setAmounts({
                        beforeAmount: dueAmount?.beforeDiscount,
                        afterAmount: dueAmount?.beforeDiscount
                    });
                    setFullEarlySettlement({
                        ...fullEarlySettlement,
                        dueAmount: dueAmount?.beforeDiscount
                    })
                }
            // eslint-disable-next-line react-hooks/exhaustive-deps
    },[isDiscountApplied,dueAmount])

    useEffect(() => {
        if (paymentType !== paymentTypes.partialSettlement || !viewAmounts) return;
        setAmounts((prev) => {
            const before = prev.beforeAmount ?? 0;
            if (before === 0) return prev;
            return {
                ...prev,
                afterAmount: Number((before - (amount ?? 0)).toFixed(2)),
            };
        });
    }, [amount, paymentType, viewAmounts]);

    const getAmounts = async (paymentCycle: number) => {
        const beforeAfterAmounts: { [key: string]: any } = await dispatch(request({
            url: getAmountsForExternalPayments, method: "GET", params: {
                userID: userInfo?.userID, paymentCycle: paymentCycle, companyID: userInfo?.companyID, gtePaymentCycle: true
            }
        }));
        if (beforeAfterAmounts?.data?.status !== 200) {
            message.error(beforeAfterAmounts?.data?.message);
            return
        }
        let newAmounts: { beforeAmount: number, afterAmount: number } = {
            beforeAmount: beforeAfterAmounts?.data?.userAmount ?? 0,
            afterAmount: Number(((beforeAfterAmounts?.data?.userAmount ?? 0)).toFixed(2)),
        }
        setViewAmounts(true);
        const userAmount = beforeAfterAmounts?.data?.userAmount ?? 0;
        const amountVal = amount ?? 0;
        if (amountVal > 0) {
            setAmounts({
                ...amounts,
                beforeAmount: beforeAfterAmounts?.data?.userAmount,
                afterAmount: Number((userAmount - amountVal).toFixed(2))
            });
        } else {
            setAmounts(newAmounts);
        }
    }

    const getInstallments = async (paymentCycle: number) => {
        try {
            const installmentsRes: { [key: string]: any } = await dispatch(request({ url: getUserInstallmentsApi, method: "GET", params: { userID: userInfo?.userID, paymentCycle } }))
            getAmounts(paymentCycle);

            if (installmentsRes?.data?.status === 200) {
                const installmentsData = Array.isArray(installmentsRes.data.installments) ?
                    installmentsRes.data.installments.map((installment: any) => ({
                        "User ID": installment["Advance.userID"],
                        "Advance ID": installment.advanceID,
                        "Service Type": installment["advanceID.serviceType"],
                        "Installment ID": installment.installmentID,
                        "Instalment Order": installment.instalmentOrder,
                        "Payment Month": helper.formatDate(installment.actualPaymentDate, 'MMMM D, YYYY, h:mm A'),
                        "Payments Amount (EGP)": installment.paymentsAmount,
                        "Processing Fees (EGP)": installment.processingFees,
                        "Discount (EGP)": installment.advanceID,
                        "Revenue (EGP)": installment.revenueAfterDiscount
                    })) : []
                handleTableChange({
                    current: 1,
                    total: installmentsRes.data.installmentsCount,
                    pageSize: 5
                })
                setInstallments(installmentsData)
                setColumns(helper.generateTableColumns(installmentsData))
            } else if (installmentsRes?.data?.status === 404) {
                message.success(installmentsRes?.data?.message)
                setInstallments([])
            }
            else {
                message.error(installmentsRes?.data?.message)
                setInstallments([])
            }
            setLoadingTableData(false)
        }
        catch (error: any) {
            message.error(errorMessage)
            setLoadingTableData(false)
            setInstallments([])
        }
    }

    const getFullInstallmentsAdvances = async (paymentCycle: number) => {
        try {
            const fullInstallmentsRes: { [key: string]: any } = await dispatch(request({
                url: getFullUserInstallmentsAdvance, method: "GET", params: {
                    userID: userInfo?.userID, paymentCycle
                }
            }))
            if (fullInstallmentsRes?.status === 200) {
                const installmentsData = Array.isArray(fullInstallmentsRes?.data?.data?.advances) ?
                    fullInstallmentsRes?.data?.data?.advances.map((installment: any) => ({
                        "User ID": installment?.userID,
                        "User Name": installment?.userName,
                        "Advance ID": installment?.advanceID,
                        "Service Name": installment?.serviceName,
                        "dateOfRequest": moment(installment?.dateOfRequest).format('Do MMM, YYYY'),
                        "requestedAmount": installment?.requestedAmount,
                        "Upcoming Amount": installment?.upcomingAmount,
                        "Upcoming Principal": installment?.upcomingPrincipal,
                        "Upcoming External Payments": installment?.upcomingExternalPayments,
                        "Upcoming Revenue": installment?.upcomingRevenue,
                        "Upcoming Discount": installment?.upcomingDiscount,
                        "Upcoming Net Revenue": installment?.upcomingNetRevenue,
                        "Six Percent Of Principal": installment?.sixPercentOfPrincipal,
                        "Early Settlement Discount": installment?.earlySettlementDiscount,
                    })) : []
                handleTableChange({
                    current: 1,
                    total: fullInstallmentsRes?.data?.data?.advances?.length ?? 0,
                    pageSize: 5
                })
                setInstallments(installmentsData)
                setColumns(helper.generateTableColumns(installmentsData))
                setFullEarlySettlement?.({
                    totalUpcomingAmount: fullInstallmentsRes?.data?.data?.totalUpcomingAmount ?? 0,
                    totalUpcomingPrincipal: fullInstallmentsRes?.data?.data?.totalUpcomingPrincipal ?? 0,
                    totalUpcomingExternalPayments: fullInstallmentsRes?.data?.data?.totalUpcomingExternalPayments ?? 0,
                    totalUpcomingRevenue: fullInstallmentsRes?.data?.data?.totalUpcomingRevenue ?? 0,
                    totalUpcomingDiscount: fullInstallmentsRes?.data?.data?.totalUpcomingDiscount ?? 0,
                    totalUpcomingNetRevenue: fullInstallmentsRes?.data?.data?.totalUpcomingNetRevenue ?? 0,
                    totalEarlySettlementDiscount: fullInstallmentsRes?.data?.data?.totalEarlySettlementDiscount ?? 0,
                    dueAmount: fullInstallmentsRes?.data?.data?.dueAmountBeforeDiscount ?? 0,
                    discountPercentageFromRevenue: fullInstallmentsRes?.data?.data?.discountPercentageFromRevenue ?? 0,
                    earlySettlementDiscountRule: fullInstallmentsRes?.data?.data?.earlySettlementDiscountRule ?? 0,
                });
                setDueAmount({
                    afterDiscount: fullInstallmentsRes?.data?.data?.dueAmount ?? 0,
                    beforeDiscount: fullInstallmentsRes?.data?.data?.dueAmountBeforeDiscount ?? 0
                });
                setViewAmounts(true);
                setLoadingTableData(false)
            } else if (fullInstallmentsRes?.status !== 200) {
                message.error(fullInstallmentsRes?.message)
                setInstallments([])
                setLoadingTableData(false)
            }
        }
        catch (error: any) {
            message.error(errorMessage)
            setLoadingTableData(false)
            setInstallments([])
        }
    }

    const handleTableChange = (e: any) => {
        setLoadingTableData(true);
        setTimeout(() => {
            setPagination({
                current: e?.current ?? 1,
                pageSize: e?.pageSize ?? 5,
                total: e?.total ?? 0,
            });
            setLoadingTableData(false);
        }, 500);
    }

    const afterAmount = useMemo(() => {
        if (paymentType === paymentTypes.fullSettlement) {
            return Number(((fullEarlySettlement?.dueAmount ?? 0) - (amount ?? 0)).toFixed(2)) ?? 0;
        }
        return amounts?.afterAmount ?? 0;
    }, [paymentType, fullEarlySettlement?.dueAmount, amount, amounts?.afterAmount]);

    return (
        <>
            <Spin size="large" spinning={loadingTableData}>
                <Row>
                    {userInfo && <Col span={24}>
                        <Descriptions bordered style={{ margin: '15px 0' }} title="User information">
                            <Descriptions.Item label="Name">{userInfo?.name ? userInfo?.name : 'NA'}</Descriptions.Item>
                            <Descriptions.Item label="Phone">{userInfo?.phone ? userInfo?.phone : 'NA'}</Descriptions.Item>
                            <Descriptions.Item label="Company">{userInfo?.companyName ? userInfo?.companyName : 'NA'}</Descriptions.Item>
                        </Descriptions>
                    </Col>}
                    {canShowSettlementInfo && viewAmounts && !loadingTableData &&
                        <Col span={24}>
                            <Descriptions bordered style={{ margin: '15px 0' }} column={3} title="Amount">
                                <Descriptions.Item label="Before amount">{paymentType === paymentTypes.fullSettlement ? (fullEarlySettlement?.dueAmount ?? 'NA') : (amounts?.beforeAmount ?? 'NA')}</Descriptions.Item>
                                <Descriptions.Item label="After amount">{afterAmount}</Descriptions.Item>
                            </Descriptions>
                            {paymentType === paymentTypes.fullSettlement &&
                                <Descriptions bordered style={{ margin: '15px 0' }} column={3} title="Full Settlement Details">
                                    {Object.entries(fullEarlySettlement || {}).map(([key, value]) => (
                                        <Descriptions.Item key={key} label={helper.unCamelCase(key)}>{String(value)}</Descriptions.Item>
                                    ))}
                                </Descriptions>
                            }
                        </Col>
                    }
                </Row>
            </Spin>
            {(canShowSettlementInfo && Array.isArray(installments) && installments.length > 0) ?
                <Row>
                    <Descriptions title="User Installments" style={{ margin: '15px 0' }}>
                    </Descriptions>
                    <Col span={24} className="btnCol">
                        <DataTable columns={columns} pagination={pagination} rows={installments} handleTableChange={handleTableChange}
                            loading={loadingTableData} scrolling={{ x: 2000 }} />
                    </Col>
                </Row>
                : null
            }
        </>
    )
}

export default UserSettlementDetails
